/**
 * Content module — surface 2.11 "Clients & AMS" (the annuity view).
 *
 * ZERO literals in the component: every figure, name, id, route and sentence
 * this surface renders is declared here or read from `@/demo/data`.
 *
 * WHAT IS SEEDED VS WHAT IS INVENTED. The base-level figures are the deck's
 * and are transcribed as given (24 contracts, €1.42m recurring, 19 of 24 on a
 * supported version, €310k of upgrade revenue booked, 6 renewals inside 90
 * days worth €395k). Bergman Winkels is the ONE seeded AMS client — its
 * version, contract value, renewal month and 30-day ticket count are read from
 * `@/demo/data` and from `@/demo/content/maintain` so the two surfaces can
 * never drift. The other six rows are tenant clients the deck implies but does
 * not name; they are invented ONCE here, with plausible offices and verticals,
 * and are anonymised only in the sense that they are fictional.
 *
 * The page shows 7 of 24 rows. The KPI row carries the totals, and the density
 * note says so — no row set is ever presented as the whole base.
 */
import type { StatusTone } from '@/components/px/StatusPill'
import { EXEMPLAR_KPIS, type Fragment } from '@/demo/content/maintain'
import { dayLabel, eur } from '@/demo/content/portfolioBoard'
import {
  NOW,
  OFFICES,
  PORTFOLIO,
  agentById,
  heroProject,
  personById,
  projectById,
  thinSpace,
  type OfficeCode,
} from '@/demo/data'

/* ── seeded anchors ─────────────────────────────────────────────────────── */

/** The one AMS client that exists in the seed — the live exemplar. */
const EXEMPLAR = projectById('bergman')
const EXEMPLAR_LEAD = personById(EXEMPLAR.leadId)
const SCOUT = agentById('upgrade-scout')

/** The release the portfolio is rolling out on — the hero project's own. */
const CURRENT_RELEASE = heroProject().odooVersion

/** A figure the Maintain surface already publishes — read, never retyped. */
const maintainFigure = (key: string): string => {
  const kpi = EXEMPLAR_KPIS.find((k) => k.key === key)
  if (!kpi) throw new Error(`Unknown maintain KPI: ${key}`)
  return kpi.figure
}

const officeLabel = (code: OfficeCode): string => {
  const office = OFFICES.find((o) => o.code === code)
  if (!office) throw new Error(`Unknown office: ${code}`)
  return `${office.code} · ${office.city}`
}

/* ── routes ─────────────────────────────────────────────────────────────── */

export const ROUTES = {
  /**
   * Bergman's AMS detail is rendered by the Maintain surface, which lives on
   * the hero project's route and carries Bergman as its live exemplar.
   */
  exemplarAms: `/project/${heroProject().id}/ams`,
  scoutConfig: `/library/agents/${SCOUT.id}`,
  playbooks: '/library/playbooks',
} as const

/* ── chrome ─────────────────────────────────────────────────────────────── */

export const MODULE_LABEL = 'PX · Projects'
export const BREADCRUMB = 'Portfolio › Clients & AMS'
export const CONTEXT_PILL = `${PORTFOLIO.ams} CONTRACTS`
export const PAGE_TITLE = 'Clients & AMS'
export const PAGE_LEDE =
  'After go-live a project becomes an annuity. Contracts, version support and renewal dates sit in one view, ' +
  'and the annual Odoo version becomes a scheduled revenue event instead of a client fire.'

/* ── 1 · the base, in four figures (deck figures, transcribed) ──────────── */

const RECURRING = 1_420_000
const SUPPORTED = 19
const UPGRADE_REVENUE = 310_000
const RENEWAL_WINDOW_DAYS = 90
const RENEWALS_DUE = 6
const RENEWALS_VALUE = 395_000

/** €1.42m — two decimals, because the base total is quoted to the 10k. */
const eurMillions = (amount: number): string => `€${(amount / 1_000_000).toFixed(2)}m`

export interface AmsKpi {
  key: string
  label: string
  figure: string
  qualifier: string
  /** True for the figure that is asking for attention this quarter. */
  urgent?: boolean
}

export const KPIS: readonly AmsKpi[] = [
  {
    key: 'contracts',
    label: 'AMS contracts',
    figure: thinSpace(PORTFOLIO.ams),
    qualifier: `${eurMillions(RECURRING)} recurring`,
  },
  {
    key: 'supported',
    label: 'On a supported version',
    figure: `${SUPPORTED} of ${PORTFOLIO.ams}`,
    qualifier: `${PORTFOLIO.ams - SUPPORTED} are past support and carry the risk`,
  },
  {
    key: 'upgrade-revenue',
    label: 'Upgrade revenue booked',
    figure: eur(UPGRADE_REVENUE),
    qualifier: 'from upgrades quoted before the release shipped',
  },
  {
    key: 'renewals',
    label: `Renewals in ${RENEWAL_WINDOW_DAYS} days`,
    figure: thinSpace(RENEWALS_DUE),
    qualifier: `${eur(RENEWALS_VALUE)} at stake`,
    urgent: true,
  },
]

/** The renewal KPI carries review weight — it is the quarter's open question. */
export const KPI_URGENT_CLASS = 'border-review/50 bg-review-bg/40'

/* ── 2 · the contracts table ────────────────────────────────────────────── */

export type SupportState = 'Current' | 'Last supported year' | 'Out of support'
export type UpgradeState = 'Quoted' | 'Draft ready' | 'Urgent'

/** Support is colour AND label — the pill never carries the meaning alone. */
export const supportTone: Record<SupportState, StatusTone> = {
  Current: 'done',
  'Last supported year': 'review',
  'Out of support': 'blocked',
}

/** Exception weight is monotonic: out of support is the only rose row. */
export const supportRowClass: Record<SupportState, string> = {
  Current: '',
  'Last supported year': '',
  'Out of support': 'bg-blocked-bg',
}

export const supportRuleClass: Record<SupportState, string> = {
  Current: 'border-l-[3px] border-l-transparent',
  'Last supported year': 'border-l-[3px] border-l-transparent',
  'Out of support': 'border-l-[3px] border-l-blocked',
}

export const upgradeTone: Record<UpgradeState, StatusTone> = {
  Quoted: 'neutral',
  'Draft ready': 'proposed',
  Urgent: 'blocked',
}

const DAY_MS = 86_400_000

/** Whole days from NOW (the demo's fixed clock) to an ISO day. */
const daysUntil = (iso: string): number =>
  Math.ceil((Date.parse(`${iso}T00:00:00Z`) - NOW.getTime()) / DAY_MS)

export interface Renewal {
  label: string
  /** Inside the 90-day window the renewals KPI counts. */
  soon: boolean
  note: string
}

const renewal = (iso: string): Renewal => {
  const days = daysUntil(iso)
  const soon = days <= RENEWAL_WINDOW_DAYS
  return {
    label: dayLabel(iso),
    soon,
    note: soon ? `in ${days} days` : `${Math.round(days / 30)} months out`,
  }
}

export interface ContractRow {
  id: string
  client: string
  /** Office · city · vertical — the row's second line. */
  subLine: string
  version: string
  support: SupportState
  ams: string
  tickets: string
  upgradeState: UpgradeState
  upgradeLabel: string
  renewal: Renewal
  /** Set only where a real detail surface exists — no dead links. */
  href?: string
  note?: string
}

/**
 * The exemplar's renewal falls in the month Maintain publishes ("renews
 * Nov 2027"), on the contract's own anniversary day — the day it went live.
 */
const EXEMPLAR_RENEWAL = `2027-11-${EXEMPLAR.goLive.slice(8, 10)}`

export const EXEMPLAR_NOTE = 'exemplar'

export const ROWS: readonly ContractRow[] = [
  {
    id: EXEMPLAR.id,
    client: EXEMPLAR.client,
    subLine: `${officeLabel(EXEMPLAR.office)} · ${EXEMPLAR.vertical} · lead ${EXEMPLAR_LEAD.name}`,
    version: `Odoo ${EXEMPLAR.odooVersion}`,
    support: 'Last supported year',
    ams: `${eur(EXEMPLAR.contractValue)}/yr`,
    tickets: maintainFigure('tickets'),
    upgradeState: 'Draft ready',
    upgradeLabel: 'Draft ready',
    renewal: renewal(EXEMPLAR_RENEWAL),
    href: ROUTES.exemplarAms,
    note: EXEMPLAR_NOTE,
  },
  {
    id: 'de-groot-interieur',
    client: 'De Groot Interieur',
    subLine: `${officeLabel('NL')} · Furniture retail`,
    version: `Odoo ${CURRENT_RELEASE}`,
    support: 'Current',
    ams: `${eur(74_000)}/yr`,
    tickets: thinSpace(22),
    upgradeState: 'Quoted',
    upgradeLabel: `Quoted ${eur(48_000)}`,
    renewal: renewal('2026-10-12'),
  },
  {
    id: 'malmberg-foods',
    client: 'Malmberg Foods',
    subLine: `${officeLabel('BE')} · Food production`,
    version: `Odoo ${CURRENT_RELEASE}`,
    support: 'Current',
    ams: `${eur(68_000)}/yr`,
    tickets: thinSpace(31),
    upgradeState: 'Draft ready',
    upgradeLabel: 'Draft ready',
    renewal: renewal('2027-03-01'),
  },
  {
    id: 'steenbergen-techniek',
    client: 'Steenbergen Techniek',
    subLine: `${officeLabel('NL')} · Industrial services`,
    version: `Odoo ${EXEMPLAR.odooVersion}`,
    support: 'Last supported year',
    ams: `${eur(58_000)}/yr`,
    tickets: thinSpace(27),
    upgradeState: 'Quoted',
    upgradeLabel: `Quoted ${eur(54_000)}`,
    renewal: renewal('2026-11-06'),
  },
  {
    id: 'delacroix-mobilier',
    client: 'Delacroix Mobilier',
    subLine: `${officeLabel('FR')} · Retail`,
    version: `Odoo ${CURRENT_RELEASE}`,
    support: 'Current',
    ams: `${eur(61_000)}/yr`,
    tickets: thinSpace(12),
    upgradeState: 'Draft ready',
    upgradeLabel: 'Draft ready',
    renewal: renewal('2027-06-30'),
  },
  {
    id: 'sint-truiden-zorg',
    client: 'Sint-Truiden Zorg',
    subLine: `${officeLabel('BE')} · Healthcare`,
    version: `Odoo ${CURRENT_RELEASE}`,
    support: 'Current',
    ams: `${eur(52_000)}/yr`,
    tickets: thinSpace(19),
    upgradeState: 'Quoted',
    upgradeLabel: `Quoted ${eur(39_000)}`,
    renewal: renewal('2027-02-15'),
  },
  {
    id: 'okerman-metaal',
    client: 'Okerman Metaal',
    subLine: `${officeLabel('BE')} · Metal fabrication`,
    version: 'Odoo 17',
    support: 'Out of support',
    ams: `${eur(44_000)}/yr`,
    tickets: thinSpace(63),
    upgradeState: 'Urgent',
    upgradeLabel: 'Urgent',
    renewal: renewal('2026-12-18'),
  },
]

export const COLUMNS = [
  { key: 'client', label: 'Client', numeric: false },
  { key: 'version', label: 'Version', numeric: false },
  { key: 'support', label: 'Support', numeric: false },
  { key: 'ams', label: 'AMS €/yr', numeric: true },
  { key: 'tickets', label: 'Tickets · 30 days', numeric: true },
  { key: 'upgrade', label: 'Upgrade', numeric: false },
  { key: 'renewal', label: 'Renewal', numeric: false },
] as const

export const TABLE = {
  label: 'Contracts',
  heading: 'The AMS base, by version and renewal date',
  caption:
    'AMS contracts — one row per client, with the version it runs, the upgrade position and the renewal date.',
} as const

/* ── 3 · the upgrade scout's standing proposal ──────────────────────────── */

/**
 * The scan follows the Maintain surface's shape — customisations scanned, split
 * into what migrates untouched, what needs rework, and what the new release has
 * made standard — but this one is Bergman's own hop, and its draft quote is the
 * figure the account manager sees on the client record.
 */
const SCAN = {
  customisations: 14,
  rework: 3,
  reworkHours: 22,
  standard: 2,
  quote: 62_000,
  deliveryDays: 11,
} as const

const UNTOUCHED = SCAN.customisations - SCAN.rework - SCAN.standard

export const SCOUT_PANEL = {
  eyebrow: `${SCOUT.name} · ${EXEMPLAR.client} · Odoo ${EXEMPLAR.odooVersion} → ${CURRENT_RELEASE}`,
  title: `${SCAN.customisations} customisations scanned`,
  meta:
    `${UNTOUCHED} migrate untouched · ${SCAN.rework} need rework ~${SCAN.reworkHours} h · ` +
    `${SCAN.standard} are now standard in Odoo ${CURRENT_RELEASE}`,
  quoteChip: `Draft quote ${eur(SCAN.quote)}`,
  daysChip: `${SCAN.deliveryDays} delivery days`,
  readyChip: 'Ready for the account manager',
  trust: 'A person sends the quote; the scout never contacts the client.',
  handoff: `Waiting on ${EXEMPLAR_LEAD.name} · ${EXEMPLAR_LEAD.role} · ${officeLabel(EXEMPLAR_LEAD.office)}`,
  agentLine: `${SCOUT.runs} · ${SCOUT.reviewGate} · ${SCOUT.acceptancePct}% of its proposals accepted`,
  governedLabel: 'How the scout is governed →',
  governedHref: ROUTES.scoutConfig,
  sectionLabel: 'Upgrade scout',
} as const

/* ── 4 · what this page withholds ───────────────────────────────────────── */

export const DENSITY_NOTE: readonly Fragment[] = [
  {
    text:
      `${ROWS.length} of ${PORTFOLIO.ams} contracts are shown — the figures above carry the whole base. ` +
      `Ticket detail sits on each project's own AMS board, and the renewal playbook — who calls, when, and ` +
      `with which upgrade quote in hand — lives in `,
  },
  { text: 'Playbooks', to: ROUTES.playbooks },
  { text: '.' },
]
